import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import { selectSelectedCarrier, selectSelectedCarrierLoading, deleteCarrier, selectDeleteCarrierLoading, selectDeleteCarrierError, resetDeleteStatus } from "../../redux/carriers/carriersSlice";
import { closeModal, setHasToRefresh, setModal } from "../../redux/rightModalSlice";
import LoadingSection from "../LoadingSection";

export default function ViewCarrier() {
    const dispatch = useDispatch();
    const selectedCarrier = useSelector(selectSelectedCarrier);
    const selectedCarrierLoading = useSelector(selectSelectedCarrierLoading);
    const deleteLoading = useSelector(selectDeleteCarrierLoading);
    const deleteError = useSelector(selectDeleteCarrierError);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const token = localStorage.getItem('installamelo-auth-token');
    
    // Handle edit button click
    const handleEditClick = () => {
        dispatch(setModal({
            mode: 'edit',
            id: selectedCarrier._id
        }));
    };
    
    // Handle delete carrier
    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };
    
    const confirmDelete = () => {
        if (selectedCarrier?._id) {
            dispatch(deleteCarrier({
                carrierId: selectedCarrier._id,
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(closeModal());
                    dispatch(setHasToRefresh());
                }
            });
        }
    };
    
    // Reset error states when component unmounts
    useEffect(() => {
        return () => {
            dispatch(resetDeleteStatus());
        };
    }, [dispatch]);
    
    if (selectedCarrierLoading) {
        return <LoadingSection />;
    }
    
    if (!selectedCarrier) {
        return (
            <div className="p-6">
                <h2 className="text-xl font-bold mb-4">Corriere non trovato</h2>
                <p className="text-gray-600">Il corriere richiesto non è disponibile.</p>
                <div className="mt-4">
                    <button className="secondary" onClick={() => dispatch(closeModal())}>
                        <DynamicIcon iconClass="fas fa-arrow-left" />
                        Torna indietro
                    </button>
                </div>
            </div>
        );
    }
    
    return (
        <div className="p-6">
            {deleteLoading && <LoadingSection isFixed />}
            
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Dettagli Corriere</h2>
                <div className="flex space-x-2">
                    <button 
                        className="secondary" 
                        onClick={handleEditClick}
                    >
                        <DynamicIcon iconClass="fas fa-edit" />
                        Modifica
                    </button>
                    <button 
                        className="danger" 
                        onClick={handleDeleteClick}
                    >
                        <DynamicIcon iconClass="fas fa-trash" />
                        Elimina
                    </button>
                </div>
            </div>
            
            <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="col-span-full">
                        <h3 className="text-sm font-medium text-gray-500">Ragione Sociale</h3>
                        <p className="text-lg font-semibold">{selectedCarrier.company || "N/A"}</p>
                    </div>
                    
                    {selectedCarrier.code && (
                        <div>
                            <h3 className="text-sm font-medium text-gray-500">Codice</h3>
                            <p className="text-lg font-semibold">{selectedCarrier.code}</p>
                        </div>
                    )}
                    
                    {selectedCarrier.vatNumber && (
                        <div>
                            <h3 className="text-sm font-medium text-gray-500">Partita IVA</h3>
                            <p className="text-lg font-semibold">{selectedCarrier.vatNumber}</p>
                        </div>
                    )}
                    
                    {selectedCarrier.fiscalcode && (
                        <div>
                            <h3 className="text-sm font-medium text-gray-500">Codice Fiscale</h3>
                            <p className="text-lg font-semibold">{selectedCarrier.fiscalcode}</p>
                        </div>
                    )}
                    
                    {selectedCarrier.address && (
                        <div className="col-span-full">
                            <h3 className="text-sm font-medium text-gray-500">Indirizzo</h3>
                            <p className="text-base">
                                {selectedCarrier.address}
                                {selectedCarrier.city && `, ${selectedCarrier.city}`}
                                {selectedCarrier.province && ` (${selectedCarrier.province})`}
                                {selectedCarrier.postalCode && ` - ${selectedCarrier.postalCode}`}
                                {selectedCarrier.state && `, ${selectedCarrier.state}`}
                            </p>
                        </div>
                    )}
                    
                    {selectedCarrier.phone && (
                        <div>
                            <h3 className="text-sm font-medium text-gray-500">Telefono</h3>
                            <p className="text-base">{selectedCarrier.phone}</p>
                        </div>
                    )}
                    
                    {selectedCarrier.mobile && (
                        <div>
                            <h3 className="text-sm font-medium text-gray-500">Cellulare</h3>
                            <p className="text-base">{selectedCarrier.mobile}</p>
                        </div>
                    )}
                    
                    {selectedCarrier.email && (
                        <div>
                            <h3 className="text-sm font-medium text-gray-500">Email</h3>
                            <p className="text-base">{selectedCarrier.email}</p>
                        </div>
                    )}
                    
                    <div>
                        <h3 className="text-sm font-medium text-gray-500">Stato</h3>
                        <div className="flex items-center space-x-2 mt-1">
                            {selectedCarrier.active !== false ? (
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                    Attivo
                                </span>
                            ) : (
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                    Non Attivo
                                </span>
                            )}
                            
                            {selectedCarrier.prospect === true && (
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                    Prospect
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="flex justify-end">
                <button className="secondary" onClick={() => dispatch(closeModal())}>
                    <DynamicIcon iconClass="fas fa-times" />
                    Chiudi
                </button>
            </div>
            
            {/* Delete confirmation dialog */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-xl font-bold mb-4">Conferma Eliminazione</h3>
                        <p className="mb-6">
                            Sei sicuro di voler eliminare il corriere "{selectedCarrier.company}"? 
                            Questa azione non può essere annullata.
                        </p>
                        
                        {deleteError && (
                            <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md">
                                <p>{deleteError}</p>
                            </div>
                        )}
                        
                        <div className="flex justify-end space-x-3">
                            <button 
                                className="secondary" 
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={deleteLoading}
                            >
                                Annulla
                            </button>
                            <button 
                                className="danger" 
                                onClick={confirmDelete}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? (
                                    <>
                                        <DynamicIcon iconClass="fas fa-spinner fa-spin" />
                                        Eliminazione...
                                    </>
                                ) : (
                                    <>
                                        <DynamicIcon iconClass="fas fa-trash" />
                                        Elimina
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
